import { ReviewDataRow } from 'models/review/ReviewDataRow/ReviewDataRow';
import { IError } from 'interfaces/review/IReviewDataRow';
import { ColumnName } from 'enums/ColumnName';
import { DataKey } from 'enums/DataKey';
import { CallTypes, HitTypes, KOrBB, IDataGridValidator, baseballLogicService, ResultTypes } from '@trackman/baseball-shared-components';

export const baseballLogicValidator: IDataGridValidator = {
  validate: (rows: ReviewDataRow[]) => {
    const result: IError[][] = [];
    let prevLivePlayIndex = -1;
    for (let i = 0; i < rows.length; i++) {
      const errors: IError[] = [];
      const row = rows[i];
      if (!row.isWarmup && !row.isDeleted) {
        const prevRow = prevLivePlayIndex >= 0 ? rows[prevLivePlayIndex] : undefined;
        validateLogic(row, prevRow, errors);
        prevLivePlayIndex = i;
      }
      result.push(errors);
    }
    return result;
  },
};

const validateLogic = (row: ReviewDataRow, prevRow: ReviewDataRow | undefined, errors: IError[]) => {
  if (Number(row.outs) > 2) {
    errors.push({
      dataKey: DataKey.Outs,
      errorMessage: 'Outs > 2',
    });
  }
  if (row.playResult === ResultTypes.Out && Number(row.outsOnPlay) === 0) {
    errors.push({
      dataKey: DataKey.OutsOnPlay,
      errorMessage: `No ${ColumnName.OutsOnPlay} but ${ColumnName.PlayResult} is ${ResultTypes.Out}`,
    });
  }
  if (Number(row.balls) > 3) {
    errors.push({
      dataKey: DataKey.Balls,
      errorMessage: 'Balls > 3',
    });
  }
  if (Number(row.strikes) > 2) {
    errors.push({
      dataKey: DataKey.Strikes,
      errorMessage: 'Strikes > 2',
    });
  }
  if (
    prevRow &&
    baseballLogicService.isBall(prevRow.pitchCall) &&
    Number(prevRow.balls) >= Number(row.balls) &&
    prevRow.batter.name === row.batter.name
  ) {
    errors.push({
      dataKey: DataKey.Balls,
      errorMessage: `${ColumnName.Balls} error`,
    });
  }
  if (prevRow && baseballLogicService.isStrike(prevRow.pitchCall, 0) && Number(row.balls) > Number(prevRow.balls)) {
    errors.push({
      dataKey: DataKey.Balls,
      errorMessage: `${ColumnName.Balls} error`,
    });
  }
  if (
    prevRow &&
    baseballLogicService.isStrike(prevRow.pitchCall, Number(prevRow.strikes)) &&
    Number(prevRow.strikes) >= Number(row.strikes) &&
    prevRow.batter.name === row.batter.name
  ) {
    errors.push({
      dataKey: DataKey.Strikes,
      errorMessage: `${ColumnName.Strikes} error`,
    });
  }
  if (prevRow && baseballLogicService.isBall(prevRow.pitchCall) && Number(row.strikes) > Number(prevRow.strikes)) {
    errors.push({
      dataKey: DataKey.Strikes,
      errorMessage: `${ColumnName.Strikes} error`,
    });
  }
  if (row.pitchCall !== CallTypes.InPlay && row.hitType !== HitTypes.Undefined) {
    errors.push({
      dataKey: DataKey.HitType,
      errorMessage: `${ColumnName.PitchCall} not '${CallTypes.InPlay}', but ${ColumnName.HitType}: '${row.hitType}'`,
    });
  }

  if (
    baseballLogicService.isBall(row.pitchCall) &&
    row.pitchCall !== CallTypes.HitByPitch &&
    Number(row.balls) === 3 &&
    row.korbb !== KOrBB.Walk
  ) {
    errors.push({
      dataKey: DataKey.KOrBB,
      errorMessage: `${ColumnName.KOrBB} error`,
    });
  }

  if (baseballLogicService.isStrike(row.pitchCall, Number(row.strikes)) && Number(row.strikes) === 2 && row.korbb !== KOrBB.Strikeout) {
    errors.push({
      dataKey: DataKey.KOrBB,
      errorMessage: `${ColumnName.KOrBB} error'`,
    });
  }

  if (
    prevRow &&
    prevRow.korbb === KOrBB.Strikeout &&
    Number(prevRow.outsOnPlay) > 0 &&
    Number(row.outs) <= Number(prevRow.outs) + Number(prevRow.outsOnPlay) &&
    prevRow.inning.inningNumber === row.inning.inningNumber &&
    prevRow.inning.isTopInning === row.inning.isTopInning
  ) {
    errors.push({
      dataKey: DataKey.Outs,
      errorMessage: `${ColumnName.Outs} not increased, but prev. pitch was a ${KOrBB.Strikeout}`,
    });
  }

  if (prevRow && (prevRow.pitchCall === CallTypes.InPlay || prevRow.pitchCall === CallTypes.HitByPitch)) {
    if (prevRow.batter.id === row.batter.id) {
      errors.push({
        dataKey: DataKey.Batter,
        errorMessage: 'Batter not changed',
      });
    }

    if (Number(row.balls) !== 0) {
      errors.push({
        dataKey: DataKey.Balls,
        errorMessage: `${ColumnName.Balls} error`,
      });
    }

    if (Number(row.strikes) !== 0) {
      errors.push({
        dataKey: DataKey.Strikes,
        errorMessage: `${ColumnName.Strikes} error`,
      });
    }
  }

  if (row.pitchCall !== CallTypes.BallIntentional && row.pitchCall !== CallTypes.HitByPitch && row.korbb === KOrBB.Walk && (Number(row.balls) < 3 || !baseballLogicService.isBall(row.pitchCall))) {
    errors.push({
      dataKey: DataKey.KOrBB,
      errorMessage: 'Walk error',
    });
  }

  if (
    prevRow &&
    Number(prevRow.outsOnPlay) > 0 &&
    Number(row.outs) <= Number(prevRow.outs) &&
    row.inning.inningNumber === prevRow.inning.inningNumber &&
    row.inning.isTopInning === prevRow.inning.isTopInning
  ) {
    errors.push({
      dataKey: DataKey.Outs,
      errorMessage: 'Outs error',
    });
  }
};
